import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Receipt, ExternalLink } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import { fmtMoney, fmtDate } from '@/lib/enums';
import InvoiceDetail from '@/pages/admin/Invoices/InvoiceDetail';

const statusBadge = (inv, t) => {
  if (inv.status === 'paid') return <Badge variant="success">{t('invoices.statusPaid')}</Badge>;
  if (inv.status === 'void') return <Badge variant="neutral">{t('invoices.statusVoid')}</Badge>;
  if (inv.overdue) return <Badge variant="danger">{t('invoices.statusOverdue')}</Badge>;
  return <Badge variant="warning">{t('invoices.statusOpen')}</Badge>;
};

/** Faturas do personal: quanto ele paga à plataforma (assinatura + comissões). */
export default function TrainerInvoices() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api
      .get('/billing/invoices/me')
      .then((res) => setRows((res.data || res).invoices || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { key: 'period', header: t('invoices.colPeriod'), render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.period}</span> },
    { key: 'studentCount', header: t('invoices.colStudents') },
    { key: 'packagesSold', header: t('invoices.colSales') },
    { key: 'subscriptionAmount', header: t('invoices.colSubscription'), render: (r) => fmtMoney(r.subscriptionAmount) },
    { key: 'commissionAmount', header: t('invoices.colCommission'), render: (r) => fmtMoney(r.commissionAmount) },
    { key: 'totalAmount', header: t('invoices.colTotal'), render: (r) => <strong style={{ color: 'var(--volt)' }}>{fmtMoney(r.totalAmount)}</strong> },
    { key: 'dueDate', header: t('invoices.colDue'), render: (r) => fmtDate(r.dueDate) },
    { key: 'status', header: t('screens.status'), render: (r) => statusBadge(r, t) },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="ghost" icon={Receipt} onClick={() => setDetail(r)}>{t('invoices.details')}</Button>
          {r.status === 'open' && r.checkoutUrl && (
            <Button size="sm" variant="outline" icon={ExternalLink} onClick={() => window.open(r.checkoutUrl, '_blank')}>
              {t('invoices.pay')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={t('nav.invoices')} />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        empty={<EmptyState icon={FileText} title={t('invoices.emptyTitle')} description={t('invoices.emptyDescTrainer')} />}
      />

      <Modal open={!!detail} onClose={() => setDetail(null)} kicker={detail?.period} title={t('nav.invoices')} size="md">
        {detail && <InvoiceDetail invoice={detail} />}
      </Modal>
    </>
  );
}
